import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Map, Compass, Trophy, ArrowRight } from 'lucide-react';
import { IslandMap } from '../components/spatial/IslandMap';
import { stakeholderEngagementIsland } from '../data/stakeholderIsland';
import type { Landmark } from '../types/spatialKnowledge';

export const StakeholderIslandExplorer: React.FC = () => {
  const [selectedLandmark, setSelectedLandmark] = useState<Landmark | null>(null);
  const [explorationStats] = useState({
    regionsVisited: 0,
    landmarksDiscovered: 0,
    totalRegions: stakeholderEngagementIsland.regions.length,
    totalLandmarks: stakeholderEngagementIsland.regions.reduce(
      (sum, r) => sum + r.landmarks.length,
      0
    )
  });

  const handleLandmarkClick = (landmark: Landmark) => {
    setSelectedLandmark(landmark);
    // TODO: Mark as discovered/visited
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-blue-50 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-4xl font-bold text-charcoal-900 mb-2 flex items-center gap-3">
              <Map className="h-10 w-10 text-consulting-slate-blue-600" />
              {stakeholderEngagementIsland.name}
            </h1>
            <p className="text-lg text-charcoal-600 max-w-2xl">
              {stakeholderEngagementIsland.description}
            </p>
          </div>
          
          {/* Progress Stats */}
          <div className="bg-[var(--bg-raised)] shadow-lg p-4 min-w-[200px]">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="h-5 w-5 text-[var(--mustard)]" />
              <h3 className="font-bold text-sm text-charcoal-900">Exploration Progress</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-charcoal-600">Regions:</span>
                <span className="font-semibold text-charcoal-900">
                  {explorationStats.regionsVisited}/{explorationStats.totalRegions}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-charcoal-600">Landmarks:</span>
                <span className="font-semibold text-charcoal-900">
                  {explorationStats.landmarksDiscovered}/{explorationStats.totalLandmarks}
                </span>
              </div>
              <div className="h-2 bg-[var(--hover-tint)] rounded-full overflow-hidden">
                <div
                  className="h-full bg-consulting-slate-blue-600 transition-all duration-500"
                  style={{
                    width: `${
                      ((explorationStats.regionsVisited + explorationStats.landmarksDiscovered) /
                        (explorationStats.totalRegions + explorationStats.totalLandmarks)) *
                      100
                    }%`
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Learning Objectives */}
        <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4 mb-6">
          <div className="flex items-start gap-3">
            <Compass className="h-6 w-6 text-consulting-slate-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-consulting-slate-blue-900 mb-2">Your Learning Journey</h3>
              <ul className="grid md:grid-cols-2 gap-2 text-sm text-consulting-slate-blue-800">
                {stakeholderEngagementIsland.learningObjectives?.map((objective, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-consulting-slate-blue-600">•</span>
                    <span>{objective}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="max-w-7xl mx-auto">
        <div className="h-[600px] mb-6">
          <IslandMap
            space={stakeholderEngagementIsland}
            onLandmarkClick={handleLandmarkClick}
          />
        </div>

        {/* Landmark Detail Panel */}
        {selectedLandmark && (
          <div className="bg-[var(--bg-raised)] shadow-2xl p-6 border-2 border-consulting-slate-blue-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-3">
                <div className="text-4xl">{selectedLandmark.icon}</div>
                <div>
                  <h2 className="text-2xl font-bold text-charcoal-900 mb-1">
                    {selectedLandmark.name}
                  </h2>
                  <div className="flex gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded text-xs font-medium">
                      {selectedLandmark.type}
                    </span>
                    <span className="px-2 py-0.5 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-xs font-medium">
                      {selectedLandmark.size}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedLandmark(null)}
                className="text-charcoal-400 hover:text-charcoal-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <p className="text-charcoal-700 mb-4">{selectedLandmark.description}</p>

            {selectedLandmark.insights && selectedLandmark.insights.length > 0 && (
              <div className="bg-sage-50 p-4 mb-4">
                <h3 className="font-bold text-sage-900 mb-2 flex items-center gap-2">
                  💡 Key Insights
                </h3>
                <ul className="space-y-1 text-sm text-sage-800">
                  {selectedLandmark.insights.map((insight, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-sage-600">→</span>
                      <span>{insight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedLandmark.frameworkId && (
              <Link
                to={`/elevation/entry/${selectedLandmark.frameworkId}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700 text-white font-medium transition-colors"
              >
                Explore This Framework
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        )}

        {/* Getting Started Guide */}
        {!selectedLandmark && (
          <div className="bg-gradient-to-r from-sage-50 to-consulting-slate-blue-50 p-6 border-2 border-sage-200">
            <h3 className="text-xl font-bold text-charcoal-900 mb-3">🗺️ How to Explore</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="font-semibold text-sage-900 mb-1">1. Navigate the Terrain</div>
                <p className="text-charcoal-600">
                  Click and drag to pan around the island. Use the zoom controls to see details or get an overview.
                </p>
              </div>
              <div>
                <div className="font-semibold text-sage-900 mb-1">2. Discover Landmarks</div>
                <p className="text-charcoal-600">
                  Click on colored regions to learn about different areas. Hover over landmarks to see what they contain.
                </p>
              </div>
              <div>
                <div className="font-semibold text-sage-900 mb-1">3. Follow Your Path</div>
                <p className="text-charcoal-600">
                  Start at Foundation Beach and work your way up to Engagement Peak. Or explore freely!
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

